import { useState } from "react"
import CustomerFeatureAccessComposer from "@/v2-backoffice-client/customer/featureAccess/CustomerFeatureAccessComposer"
import CustomerFeatureAccessHeadline from "@/v2-backoffice-client/customer/featureAccess/CustomerFeatureAccessHeadline"
import CustomerFeatureAccessUserPicker, {
  type CustomerFeatureAccessPickedUser,
} from "@/v2-backoffice-client/customer/featureAccess/CustomerFeatureAccessUserPicker"
import { useCustomerFeatureAccessUserCodesQuery } from "@/v2-backoffice-client/customer/featureAccess/customer.featureAccess.userCodes.query"
import { useLicenseSeatHoldersQuery } from "@/v2-backoffice-client/license/seats/license.seats.holders.query"
import { groupSeatProductsByUser } from "@/v2-backoffice-client/license/seats/license.seats.utils"
import type { CustomerFeatureAccessFragment } from "@/v2-backoffice-client/customer/featureAccess/__types__/customer.featureAccess.fragment"

export type CustomerFeatureAccessEditorProps = {
  customerName: string
  access: CustomerFeatureAccessFragment
}

// The headline over the package editor through the customer lens, as one
// page the tab scrolls (the editor flows; its Save footer sticks), with
// "show access for" in the headline where it means something. Owns which
// user is being previewed — the one piece of state the picker and the rows
// share — and the small reads behind it: the rule's answer for that person,
// fetched only while one is picked and separate from the access read, so
// switching people touches nothing else; and who holds a seat, which the
// headline names under each seated carrier and the picker marks beside
// each person (one read, the same the Licensing and Users tabs group).
// The picker exists only when two users of this customer can differ at
// all (a seated license, a team on the older mechanism); otherwise every
// user is the same user and the rows already show the truth for all of
// them — and there can be no seat holders, so that read is skipped too.
function CustomerFeatureAccessEditor(props: CustomerFeatureAccessEditorProps) {
  const { customerName, access } = props
  const [picked, setPicked] = useState<CustomerFeatureAccessPickedUser | null>(
    null,
  )

  const userCodes = useCustomerFeatureAccessUserCodesQuery({
    variables: { customerId: access.CustomerId, userId: picked?.id ?? 0 },
    skip: !picked,
  })
  const codes = picked
    ? userCodes.data?.getCustomerFeatureAccessUserCodes
    : null

  const seatHoldersQuery = useLicenseSeatHoldersQuery({
    variables: { customerId: access.CustomerId },
    skip: !access.UsersMayDiffer,
  })
  const seatHolders = seatHoldersQuery.data?.getLicenseSeatHolders ?? []
  const seatedProductNames = [
    ...new Set(
      access.Carriers.filter((carrier) => carrier.SeatHoldersOnly).map(
        (carrier) => carrier.ProductName,
      ),
    ),
  ]

  return (
    <div className="flex flex-col gap-3">
      <CustomerFeatureAccessHeadline
        access={access}
        customerName={customerName}
        seatHolders={seatHolders}
        picker={
          access.UsersMayDiffer ? (
            <CustomerFeatureAccessUserPicker
              customerId={access.CustomerId}
              customerName={customerName}
              picked={picked}
              onPick={setPicked}
              seatProductsByUserId={groupSeatProductsByUser(seatHolders)}
              seatedProductNames={seatedProductNames}
            />
          ) : undefined
        }
      />
      <div>
        <CustomerFeatureAccessComposer
          // Reseed every draft when what is persisted changes under the
          // editor (a save landing, another operator's edit through the
          // evict), as the package composer does.
          key={`${access.OwnList?.Id ?? 0}#${access.ClosedFeatureTypeCodes.join()}#${access.OwnList?.FeatureCount ?? 0}#${access.OwnList?.RuleCount ?? 0}`}
          customerName={customerName}
          access={access}
          userCheck={
            picked && codes ? { userName: picked.firstName, codes } : undefined
          }
        />
      </div>
    </div>
  )
}

export default CustomerFeatureAccessEditor
